import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { QzSigningKeyPanel } from "@/components/labels/QzSigningKeyPanel";
import { useEffect, useMemo, useState } from "react";
import { Settings2, Save, Languages as LanguagesIcon, ChevronRight, Star, Printer, AlertTriangle, RotateCcw } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSectionHashScroll } from "@/components/settings/useSectionHashScroll";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { DualListbox } from "@/components/DualListbox";
import { notifyApplianceCategoriesChanged } from "@/hooks/useCompanyApplianceCategories";
import {
  DEFAULT_COMPANY_LABEL_PRINTING,
  LABEL_PRINTING_QUERY_KEY,
  LABEL_PRINTING_SETTING_KEY,
  LABEL_ZPL_TEMPLATES_QUERY_KEY,
  LABEL_ZPL_TEMPLATES_SETTING_KEY,
  SHIPPED_LABEL_TEMPLATES,
  COMMON_LABEL_TEMPLATE_SIZES,
  isZebraEnvEnabled,
  labelSizeLabel,
  labelSizeOptionLabel,
  parseCompanyLabelPrintingSettings,
  parseCompanyLabelZplTemplates,
  serializeCompanyLabelZplTemplates,
  type CompanyLabelZplTemplates,
  type LabelTemplateKey,
} from "@/labels";
import type { LabelSizeId } from "@/labels";

type SettingRow = { setting_key: string; setting_value: any };

const KEYS = {
  hourlyRate: "hourly_rate",
  branding: "branding",
  applianceCategories: "appliance_categories",
  labelPrinting: LABEL_PRINTING_SETTING_KEY,
  labelZplTemplates: LABEL_ZPL_TEMPLATES_SETTING_KEY,
} as const;



// Validation schemas
const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const CURRENCY_CODE = /^[A-Z]{3}$/;
const URL_OR_EMPTY = /^(https?:\/\/[^\s]+)?$/i;

const hourlyRateSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || /^\d+(\.\d{1,2})?$/.test(v), {
    message: "Enter a number with up to 2 decimals (e.g. 65 or 65.50)",
  })
  .refine(
    (v) => {
      if (v === "") return true;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 && n <= 10000;
    },
    { message: "Hourly rate must be between 0 and 10,000" }
  );

const currencySchema = z
  .string()
  .trim()
  .regex(CURRENCY_CODE, "Use a 3-letter ISO code (e.g. EUR, USD, GBP)");

const brandNameSchema = z.string().trim().max(80, "Display name must be at most 80 characters");
const logoUrlSchema = z
  .string()
  .trim()
  .max(500, "Logo URL must be at most 500 characters")
  .regex(URL_OR_EMPTY, "Logo URL must start with http:// or https://");
const primaryColorSchema = z
  .string()
  .trim()
  .regex(HEX_COLOR, "Primary color must be a hex value like #6366f1");
const taglineSchema = z.string().trim().max(160, "Tagline must be at most 160 characters");

type FieldErrors = Partial<
  Record<"hourlyRate" | "currency" | "brandName" | "logoUrl" | "primaryColor" | "tagline", string>
>;

export default function CompanySettings() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { allowed: isAdmin, isLoading: roleLoading } = useCanManageCompany();
  const { id: companyId, name: companyName, loading: companyLoading } = useCurrentCompany();

  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [currency, setCurrency] = useState<string>("EUR");
  const [brandName, setBrandName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#6366f1");
  const [tagline, setTagline] = useState("");


  const [labelPrintingEnabled, setLabelPrintingEnabled] = useState<boolean>(DEFAULT_COMPANY_LABEL_PRINTING.enabled);
  const [labelDefaultSize, setLabelDefaultSize] = useState<LabelSizeId>(DEFAULT_COMPANY_LABEL_PRINTING.defaultLabelSize);
  const [qzCertificate, setQzCertificate] = useState<string>("");
  const [zplDrafts, setZplDrafts] = useState<CompanyLabelZplTemplates>({});
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<LabelTemplateKey>(
    SHIPPED_LABEL_TEMPLATES[0].key,
  );

  // Preselect the spare-part template for the chosen default size (display only).
  useEffect(() => {
    const match = SHIPPED_LABEL_TEMPLATES.find(
      (t) => t.kind === "spare-part" && t.size === labelDefaultSize,
    );
    if (match) setSelectedTemplateKey(match.key);
  }, [labelDefaultSize]);

  const [categories, setCategories] = useState<{ id: string; label: string }[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(true);
  useSectionHashScroll(!loading);
  const [saving, setSaving] = useState(false);

  const selectedShipped = useMemo(
    () => SHIPPED_LABEL_TEMPLATES.find((t) => t.key === selectedTemplateKey) ?? SHIPPED_LABEL_TEMPLATES[0],
    [selectedTemplateKey],
  );
  const selectedTemplateBody =
    zplDrafts[selectedTemplateKey] ?? selectedShipped.body;
  const selectedIsOverride =
    Object.prototype.hasOwnProperty.call(zplDrafts, selectedTemplateKey) &&
    (zplDrafts[selectedTemplateKey] ?? "").trim() !== selectedShipped.body.trim();


  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      const [settingsRes, catsRes] = await Promise.all([
        supabase
          .from("company_settings")
          .select("setting_key, setting_value")
          .eq("company_id", companyId)
          .in("setting_key", [
            KEYS.hourlyRate,
            KEYS.branding,
            KEYS.applianceCategories,
            KEYS.labelPrinting,
            KEYS.labelZplTemplates,
          ]),
        supabase.from("appliance_categories").select("id, name").order("name"),
      ]);
      if (settingsRes.error) {
        toast.error(settingsRes.error.message);
      } else {
        const rows = (settingsRes.data ?? []) as SettingRow[];
        const rate = rows.find((r) => r.setting_key === KEYS.hourlyRate)?.setting_value;
        const brand = rows.find((r) => r.setting_key === KEYS.branding)?.setting_value;
        const cats = rows.find((r) => r.setting_key === KEYS.applianceCategories)?.setting_value;
        if (rate && typeof rate === "object") {
          setHourlyRate(rate.amount != null ? String(rate.amount) : "");
          setCurrency(rate.currency ?? "EUR");
        }
        if (brand && typeof brand === "object") {
          setBrandName(brand.name ?? "");
          setLogoUrl(brand.logo_url ?? "");
          setPrimaryColor(brand.primary_color ?? "#6366f1");
          setTagline(brand.tagline ?? "");
        }
        if (cats && typeof cats === "object" && Array.isArray((cats as any).ids)) {
          setSelectedCategoryIds((cats as any).ids.filter((v: unknown): v is string => typeof v === "string"));
        }
        const lp = rows.find((r) => r.setting_key === KEYS.labelPrinting)?.setting_value;
        if (lp !== undefined) {
          const parsed = parseCompanyLabelPrintingSettings(lp);
          setLabelPrintingEnabled(parsed.enabled);
          setLabelDefaultSize(parsed.defaultLabelSize);
          setQzCertificate(parsed.qzCertificate);
        }
        const zpl = rows.find((r) => r.setting_key === KEYS.labelZplTemplates)?.setting_value;
        setZplDrafts(parseCompanyLabelZplTemplates(zpl));
      }
      if (catsRes.error) {
        console.warn("[appliance_categories] load failed", catsRes.error);
      } else {
        setCategories(
          (catsRes.data ?? []).map((c: { id: string; name: string }) => ({ id: c.id, label: c.name })),
        );
      }
      setLoading(false);
    })();
  }, [companyId]);

  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [loading]);




  const validate = (): { ok: boolean; values?: { rateNum: number | null; currency: string; brandName: string; logoUrl: string; primaryColor: string; tagline: string } } => {
    const next: FieldErrors = {};

    const rateRes = hourlyRateSchema.safeParse(hourlyRate);
    if (!rateRes.success) next.hourlyRate = rateRes.error.issues[0].message;

    const currencyRes = currencySchema.safeParse(currency);
    if (!currencyRes.success) next.currency = currencyRes.error.issues[0].message;

    const brandRes = brandNameSchema.safeParse(brandName);
    if (!brandRes.success) next.brandName = brandRes.error.issues[0].message;

    const logoRes = logoUrlSchema.safeParse(logoUrl);
    if (!logoRes.success) next.logoUrl = logoRes.error.issues[0].message;

    const colorRes = primaryColorSchema.safeParse(primaryColor);
    if (!colorRes.success) next.primaryColor = colorRes.error.issues[0].message;

    const taglineRes = taglineSchema.safeParse(tagline);
    if (!taglineRes.success) next.tagline = taglineRes.error.issues[0].message;

    setErrors(next);
    if (Object.keys(next).length > 0) return { ok: false };

    const trimmed = (rateRes.data as string).trim();
    return {
      ok: true,
      values: {
        rateNum: trimmed === "" ? null : Number(trimmed),
        currency: currencyRes.data as string,
        brandName: brandRes.data as string,
        logoUrl: logoRes.data as string,
        primaryColor: colorRes.data as string,
        tagline: taglineRes.data as string,
      },
    };
  };

  const handleSave = async () => {
    if (!companyId || !user) return;
    const result = validate();
    if (!result.ok || !result.values) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const v = result.values;
      const zplTemplates = serializeCompanyLabelZplTemplates(zplDrafts);
      const rows = [
        {
          company_id: companyId,
          setting_key: KEYS.hourlyRate,
          setting_value: { amount: v.rateNum, currency: v.currency },
          updated_by: user.id,
        },
        {
          company_id: companyId,
          setting_key: KEYS.branding,
          setting_value: {
            name: v.brandName,
            logo_url: v.logoUrl,
            primary_color: v.primaryColor,
            tagline: v.tagline,
          },
          updated_by: user.id,
        },
        {
          company_id: companyId,
          setting_key: KEYS.applianceCategories,
          setting_value: { ids: selectedCategoryIds },
          updated_by: user.id,
        },
        {
          company_id: companyId,
          setting_key: KEYS.labelPrinting,
          setting_value: {
            enabled: labelPrintingEnabled,
            defaultLabelSize: labelDefaultSize,
            qzCertificate: qzCertificate,
          },
          updated_by: user.id,
        },
        {
          company_id: companyId,
          setting_key: KEYS.labelZplTemplates,
          setting_value: zplTemplates,
          updated_by: user.id,
        },
      ];


      const { error } = await supabase
        .from("company_settings")
        .upsert(rows, { onConflict: "company_id,setting_key" });
      if (error) throw error;
      notifyApplianceCategoriesChanged();
      await queryClient.invalidateQueries({ queryKey: LABEL_PRINTING_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: LABEL_ZPL_TEMPLATES_QUERY_KEY });
      setZplDrafts(zplTemplates);
      toast.success("Company settings saved");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (roleLoading || companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  const errorClass = (hasError: boolean) =>
    hasError ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Settings2 className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("companySettings.title")}</h1>
        </div>
        <p className="text-muted-foreground">
          {companyName ? `Tenant overrides for ${companyName}` : "Tenant-specific overrides"}
        </p>
      </div>

      <div className="px-6 space-y-6 max-w-4xl">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* Hourly rate */}
            <section id="hourly-rate" className="bg-card rounded-xl border border-border p-5 space-y-4">
              <div>
                <h2 className="font-semibold text-foreground">Hourly rate</h2>
                <p className="text-sm text-muted-foreground">
                  Default labour rate used in cost calculations. Overrides the platform default.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="hourly_rate">Rate per hour</Label>
                  <Input
                    id="hourly_rate"
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 65 or 65.50"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    aria-invalid={!!errors.hourlyRate}
                    className={errorClass(!!errors.hourlyRate)}
                  />
                  {errors.hourlyRate && (
                    <p className="text-xs text-destructive">{errors.hourlyRate}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="currency">Currency</Label>
                  <Input
                    id="currency"
                    placeholder="EUR"
                    maxLength={3}
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                    aria-invalid={!!errors.currency}
                    className={errorClass(!!errors.currency)}
                  />
                  {errors.currency && (
                    <p className="text-xs text-destructive">{errors.currency}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Branding */}
            <section id="branding" className="bg-card rounded-xl border border-border p-5 space-y-4">
              <div>
                <h2 className="font-semibold text-foreground">Branding</h2>
                <p className="text-sm text-muted-foreground">
                  Shown on product passports and printed labels.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="brand_name">Display name</Label>
                <Input
                  id="brand_name"
                  placeholder="e.g. FixID Repairs"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  aria-invalid={!!errors.brandName}
                  className={errorClass(!!errors.brandName)}
                />
                {errors.brandName && (
                  <p className="text-xs text-destructive">{errors.brandName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="logo_url">Logo URL</Label>
                <Input
                  id="logo_url"
                  placeholder="https://..."
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  aria-invalid={!!errors.logoUrl}
                  className={errorClass(!!errors.logoUrl)}
                />
                {errors.logoUrl && (
                  <p className="text-xs text-destructive">{errors.logoUrl}</p>
                )}
                {logoUrl && !errors.logoUrl && (
                  <img
                    src={logoUrl}
                    alt="Logo preview"
                    className="mt-2 max-h-16 rounded border border-border bg-white p-1 object-contain"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="primary_color">Primary color</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="primary_color"
                    type="color"
                    value={HEX_COLOR.test(primaryColor) ? primaryColor : "#6366f1"}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-10 w-16 p-1"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#6366f1"
                    aria-invalid={!!errors.primaryColor}
                    className={`flex-1 ${errorClass(!!errors.primaryColor)}`}
                  />
                </div>
                {errors.primaryColor && (
                  <p className="text-xs text-destructive">{errors.primaryColor}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tagline">Tagline</Label>
                <Textarea
                  id="tagline"
                  placeholder="Short subtitle shown under the brand name"
                  rows={2}
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  aria-invalid={!!errors.tagline}
                  className={errorClass(!!errors.tagline)}
                />
                {errors.tagline && (
                  <p className="text-xs text-destructive">{errors.tagline}</p>
                )}
              </div>
            </section>

            {/* Appliance categories */}
            <section id="appliance-categories" className="bg-card rounded-xl border border-border p-5 space-y-4">
              <div>
                <h2 className="font-semibold text-foreground">Appliance categories</h2>
                <p className="text-sm text-muted-foreground">
                  Pick which appliance categories your company services. Items in the right column
                  will be available across workflows; drag to reorder priority.
                </p>
              </div>
              <DualListbox
                items={categories}
                value={selectedCategoryIds}
                onChange={setSelectedCategoryIds}
                availableTitle="Available Appliance Categories"
                selectedTitle="Selected Appliance Categories"
              />
              {categories.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No appliance categories defined yet. Ask a superadmin to add some in the
                  Superadmin Console.
                </p>
              )}
            </section>

            {/* Languages */}
            <button
              type="button"
              onClick={() => navigate("/settings/company/languages")}
              className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <LanguagesIcon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">{t("settings.sections.languages.title")}</p>
                <p className="text-sm text-muted-foreground">{t("settings.sections.languages.description")}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>

            {/* Label printing */}
            <section id="label-printing" className="bg-card rounded-xl border border-border p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Printer className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-foreground">Label printing</h2>
                  <p className="text-sm text-muted-foreground">
                    Enable Zebra / QZ Tray raw ZPL printing for spare-part, bin and passport labels.
                    When disabled, the app uses the browser print dialog only.
                  </p>
                </div>
              </div>

              {!isZebraEnvEnabled() && (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Zebra printing is turned off at the platform level for this deployment
                    (<code className="font-mono">VITE_ZEBRA_PRINT_ENABLED</code> is not set). You can still
                    save a policy here, but Zebra options will only appear on builds where the flag is on.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="label_printing_enabled" className="text-sm font-medium">
                    Enable Zebra printing for this company
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When off, operators only see the browser print button.
                  </p>
                </div>
                <Switch
                  id="label_printing_enabled"
                  checked={labelPrintingEnabled}
                  onCheckedChange={setLabelPrintingEnabled}
                />
              </div>

              <div className="space-y-1.5 max-w-xs">
                <Label htmlFor="label_default_size">Default label size</Label>
                <Select
                  value={labelDefaultSize}
                  onValueChange={(v) => setLabelDefaultSize(v as LabelSizeId)}
                >
                  <SelectTrigger id="label_default_size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_LABEL_TEMPLATE_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {labelSizeOptionLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Used for all label types. Pick the roll that is loaded in the printer.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="qz_certificate">QZ Tray certificate (PEM)</Label>
                <Textarea
                  id="qz_certificate"
                  rows={6}
                  value={qzCertificate}
                  onChange={(e) => setQzCertificate(e.target.value)}
                  placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                  className="font-mono text-xs"
                />
              </div>

              {companyId && (
                <QzSigningKeyPanel companyId={companyId} certificateDraft={qzCertificate} />
              )}

              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">ZPL templates</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Override shipped label layouts for this company. Placeholders must match the
                      selected label type; leave a template on the default (or reset it) to use the
                      bundled version.
                    </p>
                  </div>
                  {selectedIsOverride ? (
                    <Badge variant="secondary">Custom</Badge>
                  ) : (
                    <Badge variant="outline">Default</Badge>
                  )}
                </div>

                <div className="space-y-1.5 max-w-md">
                  <Label htmlFor="zpl_template_key">Template</Label>
                  <Select
                    value={selectedTemplateKey}
                    onValueChange={(v) => setSelectedTemplateKey(v as LabelTemplateKey)}
                  >
                    <SelectTrigger id="zpl_template_key">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIPPED_LABEL_TEMPLATES.map((t) => {
                        const overridden =
                          Object.prototype.hasOwnProperty.call(zplDrafts, t.key) &&
                          (zplDrafts[t.key] ?? "").trim() !== t.body.trim();
                        return (
                          <SelectItem key={t.key} value={t.key}>
                            {t.label}
                            {overridden ? " (custom)" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {selectedShipped.placeholders.map((name) => (
                    <Badge key={name} variant="outline" className="font-mono text-[10px] font-normal">
                      {`{{${name}}}`}
                    </Badge>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="zpl_template_body">ZPL body</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1.5 text-xs"
                      disabled={!selectedIsOverride}
                      onClick={() => {
                        setZplDrafts((prev) => {
                          const next = { ...prev };
                          delete next[selectedTemplateKey];
                          return next;
                        });
                      }}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Reset to default
                    </Button>
                  </div>
                  <Textarea
                    id="zpl_template_body"
                    rows={14}
                    value={selectedTemplateBody}
                    onChange={(e) => {
                      const value = e.target.value;
                      setZplDrafts((prev) => ({ ...prev, [selectedTemplateKey]: value }));
                    }}
                    className="font-mono text-xs"
                    spellCheck={false}
                  />
                </div>
              </div>
            </section>


            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
